import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  listProducts,
  listProductPage,
  createProduct,
  updateProduct,
  deleteProduct,
} from "./api";
import type {
  CreateProductPayload,
  ProductPageQuery,
  UpdateProductPayload,
} from "./types";

const QUERY_KEY = ["products"];
export const PRODUCTS_QUERY_LIMIT = 2000;

export function useProducts() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => listProducts(PRODUCTS_QUERY_LIMIT, 0),
  });
}

function normalizeProductPageQuery(query: ProductPageQuery): ProductPageQuery {
  const normalized = {
    limit: query.limit,
    offset: query.offset,
    search: query.search.trim(),
  };

  return query.category
    ? { ...normalized, category: query.category }
    : normalized;
}

export function useProductPage(query: ProductPageQuery, enabled = true) {
  const normalizedQuery = normalizeProductPageQuery(query);

  return useQuery({
    queryKey: [...QUERY_KEY, "page", normalizedQuery],
    queryFn: ({ signal }) => listProductPage(normalizedQuery, signal),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProductPayload) => createProduct(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateProductPayload;
    }) => updateProduct(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
